import React from 'react';
import { Typography } from 'antd';
import { FormInput } from '../../../../ui/inputs/form-input';
import { VisualSelectOption } from '../../../../../types/templates';
import { ItemCard } from './item-card';

const { Text } = Typography;

interface SearchGridProps {
  options: VisualSelectOption[];
  placeholder?: string;
  /** Returns the slot number the given option currently fills, or null. */
  getSlotForValue: (value: string) => 1 | 2 | null;
  /** Whether to render the slot-number pip on selected cards (dual-slot only). */
  showPip?: boolean;
  onCardClick: (value: string) => void;
  /** Optional cell rendered as the last child of the result grid (e.g. an upload tile). */
  trailingCell?: React.ReactNode;
}

/**
 * Generic searchable grid of option cards.
 * Handles text search, result count, and rendering. Slot-state (which cards are selected)
 * is delegated to the parent via `getSlotForValue`.
 */
export const SearchGrid: React.FC<SearchGridProps> = ({
  options,
  placeholder,
  getSlotForValue,
  showPip,
  onCardClick,
  trailingCell
}) => {
  const [query, setQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      o =>
        String(o.label ?? '')
          .toLowerCase()
          .includes(q) || String(o.value).toLowerCase().includes(q)
    );
  }, [options, query]);

  return (
    <>
      <FormInput
        size='S'
        placeholder={placeholder ?? 'Buscar...'}
        value={query}
        onChange={e => setQuery(e.target.value)}
        style={{ marginBottom: 8 }}
        allowClear
      />

      <Text type='secondary' style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
        {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
      </Text>

      <div style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 2, width: '100%' }}>
        {(filtered.length !== 0 || trailingCell) && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(75px, 1fr))',
              gap: 6
            }}
          >
            {filtered.map(opt => (
              <ItemCard
                key={String(opt.value)}
                option={opt}
                slotNum={getSlotForValue(String(opt.value))}
                showPip={showPip}
                onClick={() => onCardClick(String(opt.value))}
              />
            ))}
            {trailingCell}
          </div>
        )}
      </div>
    </>
  );
};
